/* ══════════════════════════════════════════════════════════════
   Quick Stats — AD Ratio, PCR, 52w, Delivery %, Volume
   Compact stat cards for dashboard overview
   ══════════════════════════════════════════════════════════════ */

import { fetchBreadth, fetchVIX } from '../services/market-data';
import { renderUnavailableBlock } from './data-state';

export interface QuickStatsData {
  advancers: number;
  decliners: number;
  unchanged: number;
  pcr: number | null;
  high52w: number;
  low52w: number;
  deliveryPct: number;
  totalVolume: number | null;
  vix: number | null;
}

function statCard(emoji: string, label: string, value: string, sub: string, color?: string): string {
  return `
    <div class="quick-stat-card">
      <span class="stat-emoji">${emoji}</span>
      <div class="stat-content">
        <span class="stat-label">${label}</span>
        <span class="stat-value" ${color ? `style="color:${color}"` : ''}>${value}</span>
        <span class="stat-sub">${sub}</span>
      </div>
    </div>`;
}

function formatTurnover(totalVolume: number | null): string {
  if (totalVolume === null || totalVolume <= 0) return 'N/A';
  if (totalVolume >= 100000) return `₹${(totalVolume / 100000).toFixed(2)}L Cr`;
  return `₹${totalVolume.toLocaleString('en-IN')} Cr`;
}

function renderWithData(container: HTMLElement, d: QuickStatsData): void {
  const adRatio = d.advancers === 0 && d.decliners === 0
    ? 'N/A'
    : d.decliners > 0 ? (d.advancers / d.decliners).toFixed(2) : '∞';
  const adColor = d.advancers > d.decliners ? 'var(--india-green)' : 'var(--india-red)';
  const pcrColor = d.pcr === null
    ? 'var(--text-muted)'
    : d.pcr > 1.2 ? 'var(--india-green)' : d.pcr < 0.8 ? 'var(--india-red)' : 'var(--india-saffron)';
  const vixColor = d.vix === null
    ? 'var(--text-muted)'
    : d.vix < 15 ? 'var(--india-green)' : d.vix > 25 ? 'var(--india-red)' : 'var(--india-saffron)';

  container.innerHTML = `
    <div class="glass-card quick-stats-panel">
      <div class="card-header">
        <h3>⚡ Quick Stats</h3>
      </div>
      <div class="quick-stats-grid">
        ${statCard('📊', 'AD Ratio', adRatio, `${d.advancers}A / ${d.decliners}D`, adColor)}
        ${statCard('🔮', 'PCR', d.pcr === null ? 'N/A' : d.pcr.toFixed(2), d.pcr === null ? 'options feed unavailable' : d.pcr > 1 ? 'Put heavy' : 'Call heavy', pcrColor)}
        ${statCard('📈', '52w High', String(d.high52w), 'stocks at high')}
        ${statCard('📉', '52w Low', String(d.low52w), 'stocks at low')}
        ${statCard('📦', 'Delivery', `${d.deliveryPct}%`, 'of total volume')}
        ${statCard('🌊', 'Turnover', formatTurnover(d.totalVolume), 'cash market')}
        ${statCard('😰', 'India VIX', d.vix === null ? 'N/A' : d.vix.toFixed(1), d.vix === null ? 'volatility feed unavailable' : d.vix < 15 ? 'Calm' : d.vix < 25 ? 'Cautious' : 'Fear', vixColor)}
        ${statCard('⚖️', 'Unchanged', String(d.unchanged), 'stocks flat')}
      </div>
    </div>`;
}

export function renderQuickStats(container: HTMLElement, data?: QuickStatsData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = renderUnavailableBlock('glass-card quick-stats-panel', 'Loading quick stats...');
    Promise.all([fetchBreadth(), fetchVIX()]).then(([breadth, vix]) => {
      if (breadth || vix) {
        const mapped: QuickStatsData = {
          advancers: breadth?.advances ?? 0,
          decliners: breadth?.declines ?? 0,
          unchanged: breadth?.unchanged ?? 0,
          pcr: null,
          high52w: breadth?.new52wHigh ?? 0,
          low52w: breadth?.new52wLow ?? 0,
          deliveryPct: breadth?.deliveryPercent ?? 0,
          totalVolume: breadth?.totalTurnover ?? null,
          vix: vix?.current ?? null,
        };
        renderWithData(container, mapped);
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card quick-stats-panel', 'Live quick stats unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card quick-stats-panel', 'Live quick stats unavailable');
    });
  }
}
